import { FC, ButtonHTMLAttributes } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import cn from "classnames"; // Keep classnames for flexibility

const buttonVariants = cva(
    `cursor-pointer w-full flex items-center justify-center relative border 
    rounded-full transition-colors duration-200 ease-in-out text-white 
    focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed z-10`,
    {
        variants: {
            variant: {
                connect: "bg-primary hover:bg-primary-600",
                loading: "bg-muted cursor-wait",
                error: "bg-orange-600/30 hover:bg-orange-600 border-orange-600",
                fast: "bg-green-500 hover:bg-green-600 border-green-700",
                default: "bg-primary/30 hover:bg-primary cursor-pointer border-primary",
                ton: "bg-secondary/30 hover:bg-secondary border-secondary",
                faded: "bg-card hover:bg-card border-card-foreground ",
                meme: "bg-gradient-to-r from-[#5537FF] to-[#D24078] cursor-pointer  ",
            },
            size: {
                default: "py-3 px-6",
                mobile: "p-2",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    },
);

export interface BigButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
    text: string | JSX.Element;
    showSpaceHint?: boolean;
    loading?: boolean;
}

const BigButton: FC<BigButtonProps> = ({
    className,
    variant,
    size,
    text,
    onClick,
    disabled,
    showSpaceHint,
    loading = false,
    ...props
}) => {
    return (
        <button
            onClick={onClick}
            className={cn(
                (disabled || loading)
                    ? buttonVariants({ variant: "faded", size, className })
                    : buttonVariants({ variant, size, className }),
            )}
            disabled={disabled || loading}
            {...props}
        >
            {loading ? (
                <span className="flex items-center justify-center gap-2">
                    <span className="inline-block w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Loading...</span>
                </span>
            ) : (
                <span>{text}</span>
            )}
            {showSpaceHint && (
                <span className="ml-2 bg-gray-700 text-gray-200 px-2 py-1 rounded text-xs font-mono absolute right-3">
                    space
                </span>
            )}
        </button>
    );
};

export default BigButton;
